import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Users, Play, Clock, Sun, Moon, Crown, Volume2, VolumeX, Music, Check, Tv, ExternalLink } from "lucide-react";
import { useAuth, authHeaders, getToken } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import LegalDisclaimer from "../components/LegalDisclaimer";
import NominationLottoModal from "../components/NominationLottoModal";
import ManagerName from "../components/ManagerName";
import KeeperManagementPanel from "../components/lobby/KeeperManagementPanel";
import NominationOrderPanel from "../components/lobby/NominationOrderPanel";
import PreDraftHub from "../components/lobby/PreDraftHub";
import ConfirmModal from "../components/common/ConfirmModal";
import ManagerActionMenu from "../components/common/ManagerActionMenu";
import EmptySeatModal from "../components/lobby/EmptySeatModal";
import MockDraftControls from "../components/draft/MockDraftControls";
import { prewarm, playLine, playFromBase64, enqueue, setAdamLeague } from "../audio/auctioneerAudio";
import { startAmbient, stopAmbient, setAmbientVolume, swapTrack, previewTrack, fadeOutAmbient } from "../audio/lobbyAmbient";
import { pickTimerLabel } from "../utils/pickTimer";
import PreDraftCountdownOverlay from "../components/draft/PreDraftCountdownOverlay";
import AdamVoicePicker from "../components/audio/AdamVoicePicker";

// Used only if the per-league kickoff clip failed to render.
const KICKOFF_ONSETS_FALLBACK = { three: 0, two: 0.8, one: 1.6, kicker: 2.4 };
// Ceremony safety rails: no single audio step may block longer than this, and
// the whole ceremony hard-completes at the ceiling regardless of audio state.
const AUDIO_STEP_MAX_MS = 8000;
const CEREMONY_MAX_MS = 32000;
import { resolveLobbyAutoplay, loadAudioPrefs, saveAudioPrefs } from "../audio/audioPrefs";
import TeamNameGateModal from "../components/lobby/TeamNameGateModal";
import AdminButton from "../components/AdminButton";
import { diag, diagError, diagFlush, setDiagContext } from "../utils/diag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// [CEREMONY HOTFIX audit] Server broadcasts that only need a league refresh.
// Each of these was fired-and-ignored, so a remote client only caught up on
// the next 5s poll: readiness (which gates Start), a manager arriving or
// dropping (which also gates Start via is_connected), a team name landing,
// and the lobby being closed under them.
const REFRESH_ON_BROADCAST = new Set([
  "LOBBY_READY_CHANGED",
  "MANAGER_CONNECTED",
  "MANAGER_DISCONNECTED",
  "LOBBY_TEAM_NAME_SET",
  "LOBBY_CLOSED",
]);

const isCpuManager = (mgr) =>
  mgr.first_name === "CPU" || mgr.last_name === "CPU" ||
  mgr.first_name === "SmartMoney" || mgr.first_name === "TheGrinder" || mgr.first_name === "ValueHunter";

// ── Pre-draft countdown overlay ───────────────────────────────────────────
// [ADAM AUDIO batch 2 item 2] The 10-clip escalating countdown is GONE.
// Adam says ONE static line ("The order is locked. Let the countdown
// begin!"), the visual counts 10 → 4 with Adam SILENT, and at 3 a single
// per-league clip plays ("Three! Two! One! Let the 2026 {league} draft
// begin!") with the 3/2/1 visual driven off that clip's own currentTime -
// so decode latency self-corrects instead of accumulating into a desync.
// Music stays ducked at 50% through the whole countdown.
function DraftCountdown({ firstTeam, firstLabel, leagueId, onComplete, onProgress }) {
  const [display, setDisplay] = useState(null); // null = frozen at 10 (intro phase)

  // [BOARD MIRROR] Relay the number a standalone board should be showing.
  // Absolute, never a delta, so a late or dropped frame cannot desync it.
  // `onProgress` no-ops for non-commissioners (only the pacer publishes).
  const lastSentRef = useRef(null);
  useEffect(() => {
    const n = display ?? 10;
    if (lastSentRef.current === n) return;
    lastSentRef.current = n;
    onProgress({ phase: "COUNTDOWN", countdown: n });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [display]);

  useEffect(() => {
    let active = true;
    const API_BASE = process.env.REACT_APP_BACKEND_URL;

    const fetchJson = async (url) => {
      try {
        const r = await fetch(url);
        return r.ok ? await r.json() : null;
      } catch (_e) {
        return null;
      }
    };

    // Audio is decoration. If a clip never resolves — autoplay policy, a voice
    // gate that was never satisfied, a stalled decode — the countdown must
    // carry on regardless, so every audio await is bounded.
    const bounded = async (label, promise, ms = AUDIO_STEP_MAX_MS) => {
      let timer;
      try {
        const timeout = new Promise((resolve) => {
          timer = setTimeout(() => resolve("__timeout__"), ms);
        });
        const outcome = await Promise.race([promise, timeout]);
        if (outcome === "__timeout__") {
          diagError("ceremony.audio_timeout", { step: label, ms });
        }
        return outcome;
      } catch (e) {
        diagError("ceremony.audio_threw", { step: label, error: String(e?.message || e) });
        return null;
      } finally {
        clearTimeout(timer);
      }
    };

    const run = async () => {
      diag("ceremony.countdown.start", { leagueId });
      // Warm both clips before anything plays: the static "order is
      // locked" line and this league's kickoff clip (pre-rendered at
      // lobby, so this is normally a cache hit).
      const lockedPromise = fetchJson(
        `${API_BASE}/api/tts/formulaic/countdown_order_locked?league_id=${leagueId}`,
      );
      const kickoffPromise = fetchJson(
        `${API_BASE}/api/tts/draft-kickoff/${leagueId}`,
      );

      const locked = await lockedPromise;
      if (!active) return;
      diag("ceremony.locked_clip", {
        fetched: !!locked,
        has_audio: !!locked?.audio_b64,
      });
      if (locked?.audio_b64) {
        await bounded("locked_clip", playFromBase64(locked.audio_b64, "COUNTDOWN"));
      } else {
        await bounded(
          "locked_line",
          playLine(
            locked?.text || "The order is locked. Let the countdown begin!",
            "COUNTDOWN",
          ),
        );
      }
      diag("ceremony.locked_clip.played");
      if (!active) return;

      const kickoff = await kickoffPromise;
      diag("ceremony.kickoff_clip", {
        fetched: !!kickoff,
        has_audio: !!kickoff?.audio_b64,
      });

      // Silent stretch: 10 → 4, one second per beat.
      for (let n = 10; n >= 4; n -= 1) {
        if (!active) return;
        setDisplay(n);
        await new Promise((r) => setTimeout(r, 1000));
      }
      if (!active) return;

      // At 3: one clip, and the visual follows ITS clock.
      const onsets = kickoff?.word_onsets || KICKOFF_ONSETS_FALLBACK;
      setDisplay(3);
      if (kickoff?.audio_b64) {
        await bounded("kickoff_clip", enqueue({
          group: "COUNTDOWN",
          audio_b64: kickoff.audio_b64,
          onProgress: (t) => {
            if (!active) return;
            if (t >= onsets.kicker) setDisplay(0);
            else if (t >= onsets.one) setDisplay(1);
            else if (t >= onsets.two) setDisplay(2);
          },
        }));
      } else {
        // Render failed - never leave the room in silence at "3".
        for (const n of [2, 1]) {
          await new Promise((r) => setTimeout(r, 1000));
          if (!active) return;
          setDisplay(n);
        }
        await bounded(
          "kickoff_line",
          playLine(
            kickoff?.text || "Three! Two! One! Let the draft begin!",
            "COUNTDOWN",
          ),
        );
        setDisplay(0);
      }
      if (!active) return;
      setDisplay(0);
    };

    // The ceremony's tail MUST run even if any audio await above hangs or
    // throws. It is what publishes phase LIVE (a mirroring board drops its
    // overlay off that frame) and what navigates this tab into the draft.
    // Hanging on audio previously left the board frozen on the countdown and
    // the draft never went live — audio is decoration, never a gate.
    let finished = false;
    const finish = (reason) => {
      if (finished || !active) return;
      finished = true;
      diag("ceremony.countdown.finish", { reason });
      setDisplay(0);
      try {
        fadeOutAmbient(1500);
      } catch (e) {
        diagError("ceremony.fade_failed", { error: String(e?.message || e) });
      }
      // Fire-and-forget: the draft must not wait on this, and snake leagues
      // have no nomination phase for it to begin.
      try {
        const token = localStorage.getItem("pgf_token");
        fetch(`${API_BASE}/api/commissioner/${leagueId}/begin-nomination`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {});
      } catch {
        /* non-fatal */
      }
      try {
        onProgress({ phase: "LIVE" });
      } catch (e) {
        diagError("ceremony.live_relay_failed", { error: String(e?.message || e) });
      }
      diagFlush();
      onComplete();
    };

    // Hard ceiling on the whole ceremony. 10s of visual countdown plus clip
    // render/playback; anything past this is a stall, not slow audio.
    const watchdog = setTimeout(() => finish("watchdog"), CEREMONY_MAX_MS);

    run()
      .then(() => finish("normal"))
      .catch((e) => {
        diagError("ceremony.countdown.threw", {
          error: String(e?.message || e),
          stack: String(e?.stack || "").slice(0, 400),
        });
        finish("error");
      });

    return () => {
      active = false;
      clearTimeout(watchdog);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PreDraftCountdownOverlay
      display={display}
      firstTeam={firstTeam}
      firstLabel={firstLabel}
    />
  );
}

export default function DraftLobby() {
  const { id: leagueId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showLotto, setShowLotto] = useState(false);
  const [startDraftError, setStartDraftError] = useState(null);
  const [lottoOrder, setLottoOrder] = useState([]);
  const showLottoRef = useRef(false);
  const [showLottoWaiting, setShowLottoWaiting] = useState(false);  // non-commissioner view
  const showLottoWaitingRef = useRef(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const showCountdownRef = useRef(false);
  // [HF-TD47-1] Legacy `cpuCount` / `addingCpus` state + `addCpus` handler
  // removed. [TD-66] cpu_count is now pre-set in wizard Step 4; backend
  // staggered spawn fires at /start-lobby. EmptySeatModal still surfaces at
  // /seat-status pre-flight via the start-draft check.
  // [RL-55] Empty-seat cascade state. Surfaced on Start Draft pre-flight if
  // any seats remain unfilled. Modal handles the 3 user paths (fill / adjust /
  // cancel) via callbacks defined later in this component.
  const [showEmptySeatModal, setShowEmptySeatModal] = useState(false);
  const [seatStatus, setSeatStatus] = useState(null);

  // [TD-66 L.1] Frontend mock-draft stagger stopgap removed. Backend
  // stagger (commissioner.py::_spawn_cpus_staggered) is now the source of
  // truth for both mock + live drafts. Frontend just reacts naturally to
  // streaming MANAGERS_UPDATED events.

  useEffect(() => {
    fetchLeague();
    const interval = setInterval(fetchLeague, 5000);
    return () => clearInterval(interval);
  }, [leagueId]);

  // [HF-POST-CHRIS Gap 1.A / D.9] Prime the audio context on lobby mount for
  // EVERY manager (commissioner + non-commissioner). Without this, non-
  // commissioner browsers arrive at the draft with an unprimed audio context
  // and autoplay-policy gates Adam's TTS. The mount is the deliberate user-
  // gesture entry point - they navigated here, the unlock is allowed.
  // prewarm() is idempotent under the _ready guard in auctioneerAudio.js.
  useEffect(() => {
    prewarm();
  }, []);

  // ── [TD-73] Lobby ambient audio state ─────────────────────────────────────
  const [audioVolume, setAudioVolume] = useState(() => {
    const p = loadAudioPrefs(user?.id);
    return p ? p.music_volume : 0.4;
  });
  const [audioMuted, setAudioMuted] = useState(() => {
    const p = loadAudioPrefs(user?.id);
    return p ? p.music_enabled === false : false;
  });
  const [audioSliderOpen, setAudioSliderOpen] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const audioControlRef = useRef(null);

  // Click-away to collapse the slider (Gap A.a.1).
  useEffect(() => {
    if (!audioSliderOpen) return;
    const onDocClick = (e) => {
      if (audioControlRef.current && !audioControlRef.current.contains(e.target)) {
        setAudioSliderOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [audioSliderOpen]);

  // [TD-73 Section 2.8] Autoplay + track-change WS wiring.
  // Starts ambient music when:
  //   - lobby is open (status === "LOBBY")
  //   - resolveLobbyAutoplay says shouldPlay=true (first visit OR user pref)
  // [BUG-B] Does NOT stop on the LOBBY→DRAFTING transition: the bed has to
  // survive reveal → countdown → first pick, where `fadeOutAmbient` retires
  // it. Only leaving the lobby page (unmount) hard-stops it.
  const trackKey = league?.settings?.lobby_audio_track || "pgf_stadium_anthem_full";

  // [P3] The manager names their own team before they see the lobby. Only for
  // an OPEN lobby, and only until they confirm once.
  const myManager = league?.managers?.find((m) => m.user_id === user?.id) || null;
  const needsTeamName =
    league?.status === "LOBBY" && !!myManager && !myManager.name_confirmed;

  // [P4 HOTFIX] Start Draft waits ONLY on connected, human, NON-COMMISSIONER
  // managers who haven't readied.
  //   • The commissioner has Start, not Ready - his seat could NEVER ready, so
  //     including it deadlocked EVERY draft (start-lobby resets is_ready to
  //     false for all seats, the commissioner included).
  //   • CPUs are always ready. `is_bot` alone was the only bot test here, but
  //     the rest of this page identifies CPU seats by name because legacy CPU
  //     users can exist without the flag - so both tests apply.
  //   • Absent / dropped seats never block.
  // Zero managers left to wait on => Start is enabled immediately.
  const pendingReady = (league?.managers || []).filter(
    (m) =>
      m.is_connected &&
      !m.is_bot &&
      !isCpuManager(m) &&
      !m.is_commissioner &&
      !m.is_ready,
  );
  const allPresentReady = pendingReady.length === 0;
  const iAmReady = !!myManager?.is_ready;
  const [readyBusy, setReadyBusy] = useState(false);

  const setMyReady = async (ready) => {
    setReadyBusy(true);
    try {
      await axios.post(`${API}/leagues/${leagueId}/ready`, { ready },
        { headers: authHeaders() });
      await fetchLeague();
    } finally {
      setReadyBusy(false);
    }
  };
  useEffect(() => {
    if (!league || !user) return;
    if (league.status !== "LOBBY") return;
    const decision = resolveLobbyAutoplay(user.id);
    if (!decision.shouldPlay) {
      setAudioMuted(true);
      return;
    }
    setAmbientVolume(decision.volume);
    setAudioVolume(decision.volume);
    setAudioMuted(false);
    const src = `${API}/static/audio/${trackKey}.mp3`;
    startAmbient({ src, volume: decision.volume }).then((res) => {
      if (res.blocked) setAutoplayBlocked(true);
      else setAutoplayBlocked(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [league?.status, trackKey, user?.id]);

  // Leaving the lobby entirely is the only hard stop.
  useEffect(() => () => { stopAmbient(); }, []);

  // WS-driven cross-fade when commissioner changes the track. The polling
  // fetchLeague() already refreshes league.settings; trackKey changing
  // re-runs the effect above. To make the transition smooth instead of
  // hard-cut we additionally call swapTrack here on dependency change AFTER
  // first mount.
  const lastTrackRef = useRef(trackKey);
  useEffect(() => {
    if (lastTrackRef.current === trackKey) return;
    if (league?.status !== "LOBBY") { lastTrackRef.current = trackKey; return; }
    if (audioMuted) { lastTrackRef.current = trackKey; return; }
    const src = `${API}/static/audio/${trackKey}.mp3`;
    swapTrack({ src, volume: audioVolume, fadeMs: 400 });
    lastTrackRef.current = trackKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackKey]);

  // [TD-73 Section 2.8 + Option B] One-shot WebSocket subscription to the
  // existing `/api/ws/draft/{league_id}` channel for `LOBBY_AUDIO_TRACK_CHANGED`
  // and `LOBBY_READY_CHANGED` broadcasts. Eliminates the up-to-5s
  // polling latency on commissioner track changes so the swapTrack cross-fade
  // fires within network RTT instead of the next polling tick. No reconnect
  // logic - lobby is short-lived and the 5s polling effect remains as a
  // safety net if the socket drops.
  //
  // [CEREMONY HOTFIX] Lifecycle is LOBBY *plus the pre-draft ceremony*: the
  // commissioner flips the league to DRAFTING before the countdown, so
  // closing on that flip would drop a following client mid-ceremony and it
  // would lose the COUNTDOWN / LIVE cues.
  useEffect(() => {
    if (!league?.id || !user?.id) return;
    const inCeremony = showLottoWaiting || showLotto || showCountdown;
    if (league.status !== "LOBBY" && !inCeremony) return;
    const token = getToken();
    if (!token) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backendUrl.replace("https://", "wss://").replace("http://", "ws://");
    let ws;
    try {
      ws = new WebSocket(`${wsUrl}/api/ws/draft/${league.id}?token=${token}`);
    } catch {
      return; // socket construction failed; polling will handle propagation
    }
    ws.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }
      // [P4 HOTFIX] The server already broadcasts LOBBY_READY_CHANGED on every
      // ready toggle, but nothing consumed it - the commissioner's gate only
      // updated on the next 5s poll, which reads as "Ready did nothing".
      // Refresh immediately instead.
      // [P4 + CEREMONY HOTFIX] Everything below was already being broadcast
      // by the server and consumed by nobody, leaving remote clients up to
      // 5s stale (or permanently stale, for the ceremony).
      if (REFRESH_ON_BROADCAST.has(msg?.type)) {
        fetchLeague();
        return;
      }

      // [CEREMONY HOTFIX] Every connected client must walk lobby -> reveal ->
      // countdown -> live draft in step. The commissioner's tab PACES this
      // (it owns Adam and publishes the absolute position); every other
      // client follows the broadcast and plays its OWN Adam audio.
      if (msg?.type === "PREDRAFT_PROGRESS") {
        if (isCommissionerRef.current) return; // the pacer drives itself
        const d = msg.data || {};
        if (d.phase === "REVEAL") {
          if (Array.isArray(d.order) && d.order.length) setLottoOrder(d.order);
          if (!showLottoWaitingRef.current && !showCountdownRef.current) {
            setShowLottoWaiting(true);
            showLottoWaitingRef.current = true;
          }
        } else if (d.phase === "COUNTDOWN") {
          setShowLottoWaiting(false);
          showLottoWaitingRef.current = false;
          if (!showCountdownRef.current) {
            setShowCountdown(true);
            showCountdownRef.current = true;
          }
        } else if (d.phase === "LIVE" && !showCountdownRef.current) {
          // Backstop: this client never picked up the countdown, so put it in
          // the draft NOW - a remote manager must never miss their turn.
          setShowLottoWaiting(false);
          showLottoWaitingRef.current = false;
          nav(draftFormatRef.current === "snake"
            ? `/draft-snake/${leagueId}` : `/draft/${leagueId}`);
        }
        return;
      }
      if (msg?.type !== "LOBBY_AUDIO_TRACK_CHANGED") return;
      const newKey = msg?.data?.track_key;
      if (!newKey) return;
      if (audioMuted) {
        lastTrackRef.current = newKey;
        fetchLeague();
        return;
      }
      const src = `${API}/static/audio/${newKey}.mp3`;
      swapTrack({ src, volume: audioVolume, fadeMs: 400 });
      lastTrackRef.current = newKey;
      // Refresh league so the commissioner picker's "Now Playing" pill
      // updates without waiting for the next polling tick.
      fetchLeague();
    };
    return () => {
      try { ws.close(); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [league?.id, league?.status, user?.id]);

  const handleAudioMuteToggle = () => {
    const nextMuted = !audioMuted;
    setAudioMuted(nextMuted);
    saveAudioPrefs(user?.id, { music_enabled: !nextMuted, music_volume: audioVolume });
    if (nextMuted) {
      stopAmbient();
    } else {
      const src = `${API}/static/audio/${trackKey}.mp3`;
      setAmbientVolume(audioVolume);
      startAmbient({ src, volume: audioVolume }).then((res) => {
        if (res.blocked) setAutoplayBlocked(true);
      });
    }
  };
  const handleAudioVolumeChange = (e) => {
    const v = Math.max(0, Math.min(1, parseFloat(e.target.value)));
    setAudioVolume(v);
    setAmbientVolume(v);
    saveAudioPrefs(user?.id, { music_volume: v, music_enabled: v > 0 });
    if (v === 0 && !audioMuted) setAudioMuted(true);
    if (v > 0 && audioMuted) setAudioMuted(false);
  };
  const handleAutoplayPillClick = async () => {
    const src = `${API}/static/audio/${trackKey}.mp3`;
    const res = await startAmbient({ src, volume: audioVolume });
    if (res.started) setAutoplayBlocked(false);
  };

  // Kept in refs so the lobby socket handler (a one-shot effect) reads the
  // CURRENT values instead of whatever they were when it subscribed.
  const isCommissionerRef = useRef(false);
  const draftFormatRef = useRef("auction");

  // [ADAM AUDIO batch 2 item 2] Warm this league's kickoff clip while the
  // lobby is open. The endpoint renders + caches on first call (per voice),
  // so the countdown itself is always a cache hit.
  useEffect(() => {
    if (!leagueId) return;
    // Scope every live synth to this league so /tts/speak resolves the
    // league's chosen voice instead of defaulting to Voice 1.
    setAdamLeague(leagueId);
    fetch(`${API}/tts/draft-kickoff/${leagueId}`).catch(() => {});
  }, [leagueId]);

  const fetchLeague = async (isRetry = false) => {
    try {
      const { data } = await axios.get(`${API}/leagues/${leagueId}`, { headers: authHeaders() });
      setLeague(data);

      const amCommissioner = data.commissioner_id === user?.id;
      // [CEREMONY HOTFIX] /prepare-draft persists `nomination_order_pending`
      // for AUCTION and `draft_order_pending` for SNAKE. Only the auction key
      // was read here, so a snake non-commissioner NEVER entered the waiting
      // lotto - they sat on "waiting for the commissioner to start" through
      // the whole reveal and then jumped straight into the live draft on the
      // DRAFTING flip, skipping reveal AND countdown.
      const pendingOrder = data.settings?.nomination_order_pending
        || data.settings?.draft_order_pending;
      isCommissionerRef.current = amCommissioner;
      draftFormatRef.current = (data.settings || {}).draft_format || "auction";

      // ── Commissioner: already handled via showLotto/showCountdown ──────
      // ── Non-commissioner: detect pending order → show waiting lotto ────
      if (!amCommissioner && pendingOrder?.length && !showLottoWaitingRef.current && !showLottoRef.current && !showCountdownRef.current) {
        setLottoOrder(pendingOrder);
        setShowLottoWaiting(true);
        showLottoWaitingRef.current = true;
      }

      // ── Non-commissioner: DRAFTING detected while in waiting lotto → countdown ──
      if (!amCommissioner && data.status === "DRAFTING" && showLottoWaitingRef.current && !showCountdownRef.current) {
        setShowLottoWaiting(false);
        showLottoWaitingRef.current = false;
        setShowCountdown(true);
        showCountdownRef.current = true;
        return; // don't nav yet - countdown handles it
      }

      // ── Auto-navigate if no overlays are blocking ──────────────────────
      if (data.status === "DRAFTING" && !showLottoRef.current && !showCountdownRef.current && !showLottoWaitingRef.current) {
        const fmt = (data?.settings || {}).draft_format || "auction";
        nav(fmt === "snake" ? `/draft-snake/${leagueId}` : `/draft/${leagueId}`);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (!isRetry && status === 401) {
        setTimeout(() => fetchLeague(true), 700);
      } else if (status === 403 || status === 404) {
        // Definitively not allowed here (or the league is gone).
        nav("/dashboard");
      }
      // [CEREMONY HOTFIX] Anything else - a 5xx, a timeout, a blip - must NOT
      // eject a manager from the lobby. This fired on a transient 500 during
      // the reveal and threw a following manager out to the dashboard
      // mid-ceremony. The 5s poll retries; staying put is always safer than
      // losing your seat.
    } finally {
      setLoading(false);
    }
  };

  const isDemo = league?.settings?.demo_mode;

  // Section 9 - Dedup managers by user_id before rendering (dual-device sessions)
  const uniqueManagers = Object.values(
    (league?.managers || []).reduce((acc, m) => { acc[m.user_id] = m; return acc; }, {})
  );

  // Build sorted manager list: commissioner first, then CPUs
  const sortedManagers = uniqueManagers.slice().sort((a, b) => {
    if (a.is_commissioner) return -1;
    if (b.is_commissioner) return 1;
    return 0;
  });

  // ── Actions ──────────────────────────────────────────────────────────────

  // [HF-TD47-1] `addCpus` handler + `maxCpuSlots` deleted. CPUs auto-fill
  // empty seats at draft launch via cpu_fill_enabled / EmptySeatModal cascade.

  const startLobby = async () => {
    setStarting(true);
    try {
      await axios.post(`${API}/commissioner/${leagueId}/start-lobby`, {}, { headers: authHeaders() });
      const { data } = await axios.get(`${API}/leagues/${leagueId}`, { headers: authHeaders() });
      setLeague(data);
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not open lobby");
    } finally {
      setStarting(false);
    }
  };

  const startDraft = async () => {
    setStarting(true);
    try {
      // [RL-55] Pre-flight seat check. If any empty seats remain, surface the
      // EmptySeatModal cascade instead of proceeding to draft start. Founder
      // spec: "Fill with bots / Adjust team count / Cancel". The modal owns
      // those actions; this just inspects + branches.
      const seatRes = await axios.get(
        `${API}/commissioner/${leagueId}/seat-status`,
        { headers: authHeaders() }
      );
      if (seatRes.data?.empty > 0) {
        setSeatStatus(seatRes.data);
        setShowEmptySeatModal(true);
        setStarting(false);
        return;
      }
      await _proceedToDraftStart();
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not start draft");
      setStarting(false);
    }
  };

  const _proceedToDraftStart = async () => {
    // Phase 1: generate nomination order + subscribe CPU bots (no countdown yet)
    const { data } = await axios.post(`${API}/commissioner/${leagueId}/prepare-draft`, {}, { headers: authHeaders() });
    if (data.nomination_order?.length) {
      setLottoOrder(data.nomination_order);
      setShowLotto(true);
      showLottoRef.current = true;
    } else {
      const fmt = (league?.settings || {}).draft_format || "auction";
      nav(fmt === "snake" ? `/draft-snake/${leagueId}` : `/draft/${leagueId}`);
    }
  };

  // [RL-55] EmptySeatModal callbacks. Parent owns side effects (toast/nav/refresh).
  const onEmptySeatFill = async () => {
    // Bots have been added by the modal's internal POST. Re-fetch league so
    // the lobby grid shows them, then proceed to the standard draft start.
    setShowEmptySeatModal(false);
    setStarting(true);
    try {
      const { data: lg } = await axios.get(`${API}/leagues/${leagueId}`, { headers: authHeaders() });
      setLeague(lg);
      await _proceedToDraftStart();
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not start draft");
      setStarting(false);
    }
  };

  const onEmptySeatAdjusted = async (newCount) => {
    // team_count adjusted. Re-fetch league + seat-status; if zero empty seats
    // remain, the modal can be closed by the user (they'll re-click Start
    // Draft). Founder spec: "returns to step 1" - we keep the modal open at
    // its prompt, with refreshed numbers so they see the new state.
    try {
      const { data: lg } = await axios.get(`${API}/leagues/${leagueId}`, { headers: authHeaders() });
      setLeague(lg);
      const { data: ss } = await axios.get(
        `${API}/commissioner/${leagueId}/seat-status`,
        { headers: authHeaders() }
      );
      setSeatStatus(ss);
      // If shrink covered all empties, modal can stay open; user clicks Cancel
      // to dismiss, then re-clicks Start Draft for the clean path.
    } catch (err) {
      // Non-fatal - modal stays open and the user can retry.
      console.warn("Seat refresh failed:", err);
    }
  };

  const onEmptySeatCancel = () => {
    setShowEmptySeatModal(false);
    setStarting(false);
  };

  const closeLobby = async () => {
    if (!window.confirm("Close the lobby? You can re-open it at any time.")) return;
    setStarting(true);
    try {
      await axios.post(`${API}/commissioner/${leagueId}/close-lobby`, {}, { headers: authHeaders() });
      const { data } = await axios.get(`${API}/leagues/${leagueId}`, { headers: authHeaders() });
      setLeague(data);
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not close lobby");
    } finally {
      setStarting(false);
    }
  };

  // [TD-89 v2 Verdict 7.A] Replace window.confirm with ConfirmModal primitive.
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const closeLeague = async () => {
    setClosing(true);
    try {
      await axios.delete(`${API}/leagues/${leagueId}`, { headers: authHeaders() });
      nav("/dashboard");
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not close league");
      setClosing(false);
    }
  };

  const isCommissioner = league?.commissioner_id === user?.id;
  const draftFormat = (league?.settings || {}).draft_format || "auction";
  const isSnakeLeague = draftFormat === "snake";

  // [BOARD MIRROR] The commissioner's lobby tab is the PACER: it owns Adam, so
  // only it knows when a clip actually finished. It publishes its absolute
  // position and the server fans it out to every standalone draft board (in
  // the room or remote). Everyone else's lobby keeps its own audio-driven
  // reveal and publishes nothing.
  const postPredraftProgress = (payload) => {
    if (!isCommissioner || !leagueId) return;
    axios.post(
      `${API}/commissioner/${leagueId}/predraft-progress`,
      payload,
      { headers: authHeaders() },
    ).catch(() => {});
  };

  const openDraftBoard = () => {
    window.open(
      isSnakeLeague ? `/draft-snake/${leagueId}/board` : `/draft/${leagueId}/board`,
      "_blank",
      "noopener",
    );
  };

  // Theme helpers
  const bg    = isDark ? "bg-[#111111]" : "bg-[#F0F0F0]";
  const text  = isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]";
  const muted = isDark ? "text-[rgba(255,255,255,0.45)]" : "text-[rgba(0,0,0,0.50)]";

  if (loading) return (
    <div className={`min-h-screen ${bg} flex items-center justify-center`}>
      <div className="w-7 h-7 border-2 border-[#FF6B00] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const handleTeamNameAccepted = async () => {
    await fetchLeague();
    // [P5] Accept is a real user gesture, so it is also the moment a blocked
    // autoplay can legally start. A no-op when audio is already playing.
    if (autoplayBlocked) {
      const res = await startAmbient({
        src: `${API}/static/audio/${trackKey}.mp3`, volume: audioVolume,
      });
      if (!res.blocked) { setAutoplayBlocked(false); setAudioMuted(false); }
    }
  };

  return (
    <>
      {/* [P3] Blurred lobby behind the entry gate. */}
      {needsTeamName && (
        <TeamNameGateModal
          leagueId={leagueId}
          leagueName={league?.name || ""}
          currentName={myManager?.team_name || ""}
          onAccept={handleTeamNameAccepted}
          onCancel={() => nav("/dashboard")}
        />
      )}
    <div
      className={`min-h-screen ${bg} flex flex-col`}
      style={needsTeamName ? { filter: "blur(7px)", pointerEvents: "none" } : undefined}
      data-gated={needsTeamName ? "true" : undefined}
    >
      {/* Demo Mode Banner */}
      {isDemo && (
        <div className="bg-[#FF6B00] text-white text-center py-2 px-4 shrink-0 font-display font-black text-sm uppercase tracking-widest">
          Demo Mode - Progress is not saved
        </div>
      )}

      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="stadium-orb-green animate-stadium-pulse" />
        <div className="stadium-orb-orange" />
      </div>

      {/* Header */}
      <header className={`relative z-40 border-b backdrop-blur-xl ${isDark ? "bg-[#111111]/88 border-white/[0.06]" : "bg-[#F0F0F0]/92 border-black/[0.06]"}`}>
        <div className="max-w-5xl mx-auto w-full px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#FF6B00] px-2 py-0.5"><span className="font-display font-black text-white text-sm uppercase tracking-wider">PGF</span></div>
            <span className={`font-display font-black text-sm uppercase tracking-widest ${text}`}>Draft Lobby</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={toggleTheme} className={`w-8 h-8 rounded-xl flex items-center justify-center border transition-all ${isDark ? "bg-white/[0.06] border-white/[0.10]" : "bg-black/[0.06] border-black/[0.08]"} hover:border-[#FF6B00]/40`}>
              {isDark ? <Sun size={14} className="text-[#F5F5F5]" /> : <Moon size={14} className="text-[#0A0A0A]" />}
            </button>
            {/* [TD-73 Section 2.6] Lobby audio control - collapsed mute icon
                with click-to-expand inline volume slider. Sequence: theme,
                audio, AdminButton, Dashboard. Per Gap H.1 header is global-
                controls only. */}
            <div
              ref={audioControlRef}
              data-testid="lobby-audio-control-root"
              className="flex items-center gap-2"
            >
              {audioSliderOpen && (
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={audioVolume}
                  onChange={handleAudioVolumeChange}
                  data-testid="lobby-audio-volume-slider"
                  aria-label="Lobby music volume"
                  className="w-20 accent-[#FF6B00] cursor-pointer"
                />
              )}
              <button
                onClick={() => setAudioSliderOpen((o) => !o)}
                onDoubleClick={handleAudioMuteToggle}
                data-testid="lobby-audio-mute-btn"
                aria-label={audioMuted ? "Unmute lobby music" : "Mute lobby music"}
                title={audioMuted ? "Lobby music muted (click to open volume; double-click to unmute)" : "Lobby music (click for volume; double-click to mute)"}
                className={`w-8 h-8 rounded-xl flex items-center justify-center border transition-all ${isDark ? "bg-white/[0.06] border-white/[0.10]" : "bg-black/[0.06] border-black/[0.08]"} hover:border-[#FF6B00]/40`}
              >
                {audioMuted
                  ? <VolumeX size={14} className={isDark ? "text-[#888]" : "text-[#666]"} />
                  : <Volume2 size={14} className={isDark ? "text-[#F5F5F5]" : "text-[#0A0A0A]"} />}
              </button>
            </div>
            {autoplayBlocked && (
              <button
                onClick={handleAutoplayPillClick}
                data-testid="lobby-audio-autoplay-pill"
                className="px-3 py-1 rounded-full bg-[#FF6B00] hover:bg-[#FF8533] text-white font-body text-xs font-medium transition-colors"
              >
                Click to enable lobby audio
              </button>
            )}
            <AdminButton variant={isDark ? "dark" : "light"} />
            <button onClick={() => nav(isDemo ? "/demo" : "/dashboard")} className={`${muted} hover:text-[#FF6B00] font-body text-sm transition-colors`}>
              ← {isDemo ? "Back to Demo Setup" : "Dashboard"}
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-5 py-8 w-full flex-1">
        {/* League header */}
        <div className="text-center mb-10">
          <div className="flex justify-center items-center gap-2 mb-3">
            <div className={`w-2 h-2 rounded-full animate-live-blip ${league?.status === "SETUP" ? "bg-[rgba(255,255,255,0.30)]" : "bg-[#00C853]"}`} />
            <span className={`font-body text-sm font-medium uppercase tracking-wider ${league?.status === "SETUP" ? muted : "text-[#00C853]"}`}>
              {league?.status === "SETUP" ? "League Setup" : "Lobby Open"}
            </span>
          </div>
          <h1 className={`font-display font-black text-5xl uppercase mb-2 ${text}`}>{league?.name}</h1>
          <p className={`font-body ${muted}`}>
            {isDemo
              ? "The room is packed. Ready to draft."
              : league?.status === "SETUP"
              ? "Open the lobby to let managers join."
              : "Waiting for managers to join..."}
          </p>

          {/* [BOARD MIRROR item 1] Open the big board early - every manager,
              not just the commissioner. Opens the existing board route in a
              new tab, where it scaffolds pre-draft and then mirrors the order
              reveal and the countdown. Silent: Adam stays in this tab. */}
          <button
            onClick={openDraftBoard}
            data-testid="lobby-open-draft-board-btn"
            className="mt-6 inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-[#FF6B00] text-white font-display font-black text-base uppercase tracking-widest transition-transform hover:scale-[1.03] active:scale-[0.99]"
            style={{ boxShadow: "0 0 34px rgba(255,107,0,0.30)" }}
          >
            <Tv size={17} /> Open Draft Board
            <ExternalLink size={13} className="opacity-70" />
          </button>
          <p className={`font-body text-xs mt-2 ${muted}`}>
            Opens in a new tab for the big screen. Keep this tab for the audio.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {/* Manager list */}
          <div className="glass-card rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className={`font-display font-bold text-lg uppercase flex items-center gap-2 ${text}`}>
                <Users size={17} className="text-[#00C853]" /> Managers
              </h2>
              <span className={`font-display font-bold text-sm ${muted}`}>
                {`${sortedManagers.length} / ${league?.settings?.num_teams || 12}`}
              </span>
            </div>

            <div className="space-y-2">
              <AnimatePresence>
                {sortedManagers.map((mgr, i) => {
                  const isMe = mgr.user_id === user?.id || mgr.is_commissioner;
                  const isCPU = isCpuManager(mgr);
                  return (
                    <motion.div
                      key={mgr.id}
                      initial={{ opacity: 0, x: -16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ type: "spring", stiffness: 300, damping: 28 }}
                      className="flex items-center justify-between bg-[#252525] rounded-lg px-3 py-2.5"
                      data-testid={`lobby-manager-${i}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-display font-black ${isMe ? "bg-[#FF6B00]" : "bg-[#333]"}`}>
                          {isMe ? <Crown size={12} className="text-white" /> : (i + 1)}
                        </div>
                        <div>
                          <p className="font-body text-sm text-[#F5F5F5] font-medium flex items-center gap-1.5">
                            <ManagerName name={mgr.team_name} isBot={mgr.is_bot || isCPU} iconSize={11} testId={`lobby-mgr-${mgr.user_id}`} />
                            {/* [P4] Ready checkmark, right of the team name. */}
                            {mgr.is_ready && !isCPU && (
                              <Check
                                size={13}
                                className="text-[#00C853] shrink-0"
                                data-testid={`lobby-ready-check-${mgr.user_id}`}
                              />
                            )}
                          </p>
                          {/* Bottom label: "You" for human, "CPU Opponent" for bots */}
                          <p className="font-body text-xs text-[rgba(255,255,255,0.40)]">
                            {isMe ? "You" : isCPU ? "CPU Opponent" : `${mgr.first_name} ${mgr.last_name}`}
                          </p>
                        </div>
                      </div>
                      {mgr.is_commissioner && (
                        <span className="text-[#FF6B00] font-display font-bold text-xs uppercase">Commissioner</span>
                      )}
                      {/* [TD-89 v2 Verdict O.A] Per-manager kebab - commissioner can
                          Convert this manager to CPU. Hidden by ManagerActionMenu
                          internals when target is self, already CPU, or status
                          blocks (SEASON_COMPLETE / ARCHIVED). */}
                      {isCommissioner && !isMe && (
                        <ManagerActionMenu
                          league={league}
                          manager={{
                            user_id: mgr.user_id,
                            first_name: mgr.first_name,
                            last_name: mgr.last_name,
                            team_name: mgr.team_name,
                            is_cpu: mgr.is_bot || isCPU,
                          }}
                          isCommissioner={isCommissioner}
                          isDark={true}
                          onMutated={fetchLeague}
                        />
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>

              {/* Empty slots - backend stagger streams CPUs in via WS, then
                  humans fill remaining seats via invite link. Empties here
                  represent seats waiting for humans (CPUs already arrived). */}
              {Array.from({ length: Math.max(0, (league?.settings?.num_teams || 12) - sortedManagers.length) }).map((_, i) => (
                <div key={`empty-${i}`} className="flex items-center gap-2 bg-[#252525]/50 rounded-lg px-3 py-2.5 border border-dashed border-[#333]">
                  <div className="w-6 h-6 rounded-full bg-[#333] border border-dashed border-[#444] flex items-center justify-center">
                    <span className="text-[#555] text-xs">+</span>
                  </div>
                  <p className="font-body text-xs text-[#555]">Waiting for manager...</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right panel */}
          <div className="space-y-4">
            {/* [TD-66 Section 2.5] "Manage Empty Seats" panel deleted:
                Spawn CPUs Now / Disable CPU Fill / Enable CPU Fill buttons +
                the panel header are gone. CPU pre-seating moved to wizard
                Step 4 (cpu_count stepper). Backend stagger fires at
                /start-lobby. Empties at Start Draft surface via the existing
                EmptySeatModal pre-flight cascade. */}

            {/* [TD-73 Section 2.7] Lobby Music track picker - commissioner-
                only, available pre-draft. Mounted in the content body per
                Gap H.1 (header is global-controls only). */}
            {isCommissioner && !isDemo &&
             ["SETUP", "LOBBY"].includes(league?.status) && (
              <div
                className={`glass-card rounded-2xl p-5`}
                data-testid="lobby-track-picker-root"
              >
                <h2 className={`font-display font-bold text-base uppercase flex items-center gap-2 mb-3 ${text}`}>
                  <Music size={16} className="text-[#FF6B00]" /> Lobby Music
                </h2>
                <p className={`font-body text-xs ${muted} mb-3`}>
                  Picks the ambient track managers hear in the lobby. Change at any time.
                </p>
                <div className="flex flex-col gap-2">
                  {[
                    { key: "pgf_stadium_anthem_full", label: "PGF Stadium Anthem - Full" },
                    { key: "pgf_stadium_anthem_marching_band", label: "PGF Stadium Anthem - Marching Band" },
                  ].map((t) => {
                    const isActive = trackKey === t.key;
                    // [HF-TD89 Bug 1 refinement] Single inline pill - only
                    // "Now Playing" survives, gated on league.status === "LOBBY".
                    // The right-side select button conveys selection state
                    // via its own SELECT/SELECTED label, so the legacy inline
                    // "Selected" pill was redundant and is dropped entirely.
                    const isPlayingInLobby = league?.status === "LOBBY";
                    return (
                      <div
                        key={t.key}
                        data-testid={`lobby-track-tile-${t.key}`}
                        className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 border ${isActive ? "border-[#FF6B00] bg-[#FF6B00]/[0.08]" : isDark ? "border-white/[0.10] bg-white/[0.03]" : "border-black/[0.10] bg-black/[0.03]"}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`font-body text-sm truncate ${text}`}>{t.label}</span>
                          {isActive && isPlayingInLobby && (
                            <span data-testid={`lobby-track-now-playing-${t.key}`} className="text-[10px] uppercase tracking-wider font-display font-bold text-[#FF6B00] px-1.5 py-0.5 rounded bg-[#FF6B00]/[0.12]">
                              Now Playing
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => {
                              const src = `${API}/static/audio/${t.key}.mp3`;
                              previewTrack(src, 5000);
                            }}
                            data-testid={`lobby-track-preview-${t.key}`}
                            className={`px-2 py-1 rounded-md font-display font-bold text-[10px] uppercase tracking-wider transition-all ${isDark ? "bg-white/[0.06] hover:bg-white/[0.12] text-[#F5F5F5]" : "bg-black/[0.06] hover:bg-black/[0.12] text-[#0A0A0A]"}`}
                          >
                            Preview
                          </button>
                          <button
                            disabled={isActive}
                            onClick={async () => {
                              try {
                                await axios.post(
                                  `${API}/commissioner/${league.id}/set-lobby-audio-track`,
                                  { track_key: t.key },
                                  { headers: authHeaders() },
                                );
                              } catch {/* surfaced via lobby refresh */}
                            }}
                            data-testid={`lobby-track-select-${t.key}`}
                            className={`px-2 py-1 rounded-md font-display font-bold text-[10px] uppercase tracking-wider transition-all disabled:opacity-40 disabled:cursor-not-allowed ${isActive ? "bg-[#FF6B00]/[0.20] text-[#FF6B00]" : "bg-[#FF6B00] hover:bg-[#FF8533] text-white"}`}
                          >
                            {isActive ? "Selected" : "Select"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* [ADAM AUDIO batch 2 item 4] Adam voice choice sits with
                    the music choice - both are per-league audio settings the
                    commissioner owns. Also mounted in the in-draft
                    commissioner controls for a mid-draft switch. */}
                <AdamVoicePicker
                  leagueId={league.id}
                  selectedVoiceId={league?.settings?.adam_voice_id}
                  isDark={isDark}
                  onSelected={() => fetchLeague()}
                />
              </div>
            )}

            {/* [Phase 2b-D §6.5.1 R6] Keeper Management Panel - commissioner only,
                pre-draft only, when use_keepers=true and seats are filled. */}
            {isCommissioner && !isDemo &&
             ["SETUP", "LOBBY"].includes(league?.status) &&
             league?.settings?.use_keepers === true &&
             sortedManagers.length === (league?.settings?.num_teams || 12) && (
              <KeeperManagementPanel league={league} managers={sortedManagers} />
            )}

            {/* [Phase 2b-D §6.5.2 R5] Nomination Order Panel - commissioner only,
                pre-draft only, when nomination_order_mode=preset and seats filled. */}
            {isCommissioner && !isDemo &&
             ["SETUP", "LOBBY"].includes(league?.status) &&
             league?.settings?.nomination_order_mode === "preset" &&
             sortedManagers.length === (league?.settings?.num_teams || 12) && (
              <NominationOrderPanel league={league} managers={sortedManagers} />
            )}

            {/* Settings */}
            {/* [HF2-2 v1.1] Draft Type label first; fields branch on draft_format.
              * Backwards-compat key fallback: wizard writes `bidding_timer_seconds`
              * / `nomination_timer_seconds` / `pick_timer_seconds`; pre-2c-D leagues
              * may still have legacy `bid_timer` / `nomination_timer` keys. The ??
              * chain reads wizard keys first, falls back to legacy, then default.
              * This fixes a pre-existing latent bug where auction lobbies have been
              * showing fallback defaults (30s / 60s) regardless of wizard values. */}
            <div className="glass-card rounded-2xl p-5">
              <h2 className={`font-display font-bold text-base uppercase flex items-center gap-2 mb-4 ${text}`}>
                <Clock size={16} className="text-[#FF6B00]" /> Draft Settings
              </h2>
              <div className="grid grid-cols-2 gap-2">
                {(() => {
                  const fmt = (league?.settings?.draft_format || "auction").toLowerCase();
                  const isSnake = fmt === "snake";
                  const scoring = (league?.settings?.scoring_format || "half_ppr").replace("_", " ").toUpperCase();
                  const roster = league?.settings?.roster_size || 14;
                  const teams = league?.settings?.num_teams || 12;
                  const bidTimer =
                    league?.settings?.bidding_timer_seconds ??
                    league?.settings?.bid_timer ??
                    30;
                  const nomTimer =
                    league?.settings?.nomination_timer_seconds ??
                    league?.settings?.nomination_timer ??
                    60;
                  const pickTimer = pickTimerLabel(
                    league?.settings?.pick_timer_enabled,
                    league?.settings?.pick_timer_seconds ?? 90,
                  );
                  const rows = isSnake
                    ? [
                        { label: "Draft Type", val: "Snake" },
                        { label: "Format", val: scoring },
                        { label: "Pick Timer", val: pickTimer },
                        { label: "Roster", val: roster },
                        { label: "Teams", val: teams },
                      ]
                    : [
                        { label: "Draft Type", val: "Auction" },
                        { label: "Format", val: scoring },
                        { label: "Budget", val: `$${league?.settings?.budget || 200}` },
                        { label: "Bid Timer", val: `${bidTimer}s` },
                        { label: "Nom Timer", val: `${nomTimer}s` },
                        { label: "Roster", val: roster },
                        { label: "Teams", val: teams },
                      ];
                  return rows.map((s) => (
                    <div
                      key={s.label}
                      data-testid={`draft-setting-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`rounded-lg px-3 py-2 ${isDark ? "bg-[rgba(255,255,255,0.05)]" : "bg-[rgba(0,0,0,0.04)]"}`}
                    >
                      <p className={`font-body text-[10px] uppercase tracking-wider ${muted}`}>{s.label}</p>
                      <p className={`font-display font-bold text-sm uppercase mt-0.5 ${text}`}>{s.val}</p>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* [TD-68 Verdict 1.A] Pre-Draft Hub replaces the legacy Invite
                Link card. Carries forward the `lobby-copy-invite-btn` testid
                inside PreDraftHub for contract continuity. Demo mode drops
                the surface entirely - demo users don't need a fake invite
                UX. */}
            {!isDemo && (
              <PreDraftHub
                league={league}
                isCommissioner={isCommissioner}
                isDark={isDark}
              />
            )}

            {/* [HF-TD47-1] Legacy "Add CPU Managers" upfront-commit card
                deleted. Founder-locked flow: commissioner sets num_teams in
                settings → "Open Lobby" → invite link goes live → humans
                join → CPUs auto-fill remaining empties at draft launch via
                cpu_fill_enabled (default) or the EmptySeatModal cascade. No
                explicit pre-lobby CPU-add step. */}

            {/* Commissioner action button - context-aware */}
            {isCommissioner && league?.status === "SETUP" && (
              <button data-testid="open-lobby-btn" onClick={startLobby} disabled={starting}
                className="w-full bg-[#00C853] hover:bg-[#00E676] disabled:opacity-50 text-[#111111] font-display font-black uppercase tracking-wider py-4 rounded-xl text-lg transition-all active:scale-95 flex items-center justify-center gap-3"
                style={{ boxShadow: "0 0 30px rgba(0,200,83,0.25)" }}>
                <Play size={20} fill="#111111" />
                {starting ? "Opening..." : "Open Lobby"}
              </button>
            )}

            {/* [TD-47.5 Gap 15] Empty-seats hint chip - replaces the deleted
                upfront-commit card with a reassurance chip. Commissioners see
                a quiet info nudge that empties are accounted for; chip
                disappears when seats are full. Subtle gray-on-dark / dark-on-
                light palette - informational, not action-orange. Renders for
                BOTH SETUP and LOBBY states (covers both CTAs above). */}
            {isCommissioner && !isDemo &&
              ["SETUP", "LOBBY"].includes(league?.status) &&
              (() => {
                const emptySeats = Math.max(
                  0,
                  (league?.settings?.num_teams || 12) - (league?.managers?.length || 0),
                );
                if (emptySeats === 0) return null;
                return (
                  <p
                    data-testid="empty-seats-hint"
                    className={`font-body text-xs text-center py-2 px-3 rounded-lg ${
                      isDark
                        ? "text-[rgba(255,255,255,0.50)] bg-white/[0.04]"
                        : "text-[rgba(0,0,0,0.55)] bg-black/[0.04]"
                    }`}
                  >
                    {emptySeats} {emptySeats === 1 ? "seat" : "seats"} waiting for humans
                  </p>
                );
              })()}

            {isCommissioner && league?.status === "LOBBY" && (
              <div className="space-y-3">
                <motion.button
                  data-testid="start-draft-btn"
                  onClick={startDraft}
                  disabled={starting || !allPresentReady}
                  title={allPresentReady ? undefined : "Waiting on managers to hit Ready"}
                  className="w-full bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-50 text-white font-display font-black uppercase tracking-wider py-4 rounded-xl text-lg transition-all active:scale-95 flex items-center justify-center gap-3"
                  style={{ boxShadow: "0 0 30px rgba(255,107,0,0.3)" }}
                >
                  <Play size={20} fill="white" />
                  {starting ? "Starting..." : "Start Draft"}
                </motion.button>
                {/* [P4] Only connected, human, non-commissioner managers gate. */}
                {!allPresentReady && (
                  <p
                    className="font-body text-xs text-center text-[rgba(255,255,255,0.45)]"
                    data-testid="start-draft-waiting-on-ready"
                  >
                    Waiting on{" "}
                    {pendingReady.length} manager
                    {pendingReady.length === 1 ? "" : "s"} to hit Ready
                  </p>
                )}
                <button data-testid="close-lobby-btn" onClick={closeLobby} disabled={starting}
                  className={`w-full border font-body text-sm py-2.5 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-40 ${isDark ? "border-[#333] text-[rgba(255,255,255,0.40)] hover:text-[#F5F5F5] hover:border-[#555]" : "border-black/[0.10] text-[rgba(0,0,0,0.45)] hover:text-[#0A0A0A]"}`}>
                  <span className="text-xs">&#x2715;</span> Close Lobby
                </button>
              </div>
            )}

            {/* [P4] A present manager arms themselves READY where the
                commissioner's Start Draft sits. */}
            {!isCommissioner && league?.status === "LOBBY" && (
              <div className="space-y-3">
                <motion.button
                  data-testid="lobby-ready-btn"
                  onClick={() => setMyReady(!iAmReady)}
                  disabled={readyBusy || needsTeamName}
                  className={`w-full font-display font-black uppercase tracking-wider py-4 rounded-xl text-lg transition-all active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50 ${
                    iAmReady
                      ? "bg-[#00C853] hover:bg-[#00A846] text-[#0A0A0A]"
                      : "bg-transparent border-2 border-[#00C853] text-[#00C853] hover:bg-[#00C853]/10"
                  }`}
                  style={iAmReady ? { boxShadow: "0 0 30px rgba(0,200,83,0.35)" } : undefined}
                >
                  <Check size={20} />
                  {iAmReady ? "You're Ready" : "Ready"}
                </motion.button>
                <p className="text-[#A0A0A0] font-body text-xs text-center">
                  {iAmReady
                    ? "Waiting for the commissioner to start the draft..."
                    : "Hit Ready so the commissioner can start."}
                </p>
              </div>
            )}
            {!isCommissioner && league?.status !== "LOBBY" && (
              <div className="bg-[#252525] rounded-xl p-4 text-center">
                <p className="text-[#A0A0A0] font-body text-sm">Waiting for the commissioner to start the draft...</p>
              </div>
            )}

            {/* [Hotfix Bundle v1.1 §4 HF-3] Mock commissioners can delete their
                throwaway leagues from the lobby. Renders only the Delete button
                here (Stop is for active drafts only). */}
            {isCommissioner && (
              <MockDraftControls
                leagueId={leagueId}
                league={league}
                draftState={{ state: league?.status }}
              />
            )}
          </div>
        </div>
      </main>

      <div className={`relative z-10 py-4 px-5 border-t ${isDark ? "border-white/[0.06]" : "border-black/[0.06]"}`}>
        <LegalDisclaimer />
      </div>

      {/* [ADAM AUDIO batch 2 item 1] ONE audio-gated reveal for every
          client - the two mounts (commissioner animated / everyone else
          instantly revealed and silent) are gone. Only the commissioner
          gets the advance button. */}
      <AnimatePresence>
        {(showLotto || showLottoWaiting) && league?.managers && lottoOrder.length > 0 && (
          <NominationLottoModal
            nominationOrder={lottoOrder}
            managers={league.managers}
            myUserId={user?.id}
            format={(league?.settings || {}).draft_format || "auction"}
            canAdvance={showLotto}
            onRevealProgress={(revealedCount) => postPredraftProgress({
              phase: "REVEAL",
              revealed_count: revealedCount,
              order: lottoOrder,
            })}
            onComplete={async () => {
              setShowLotto(false);
              showLottoRef.current = false;
              prewarm();
              // Arming the draft MUST succeed before the ceremony proceeds.
              // This used to be `catch {}`: a failed start-draft still ran the
              // countdown and navigated everyone into a draft that was never
              // armed — no picker on the clock, no pick timer, so nobody could
              // select a player and no auto-pick ever fired.
              try {
                const res = await axios.post(
                  `${API}/commissioner/${leagueId}/start-draft`,
                  { nomination_order: lottoOrder },
                  { headers: authHeaders() }
                );
                diag("lobby.start_draft.ok", { status: res.status, order_len: lottoOrder.length });
              } catch (e) {
                const detail = e?.response?.data?.detail || e?.message || "unknown";
                diagError("lobby.start_draft.failed", {
                  status: e?.response?.status || 0,
                  detail: String(detail),
                  order_len: lottoOrder.length,
                });
                diagFlush();
                setStartDraftError(String(detail));
                return; // no countdown, no navigation — the draft is not live
              }
              setShowCountdown(true);
              showCountdownRef.current = true;
            }}
          />
        )}
      </AnimatePresence>

      {/* Pre-draft countdown overlay - shows after lotto, before navigating */}
      <AnimatePresence>
        {startDraftError && (
          <div
            data-testid="lobby-start-draft-error"
            className="fixed inset-x-0 bottom-6 mx-auto w-[min(92vw,560px)] z-[120] rounded-xl border border-[#FF3B30]/60 bg-[#1C1C1C] px-5 py-4 shadow-2xl"
          >
            <p className="font-display font-bold text-base uppercase tracking-wide text-[#FF3B30]">
              Draft failed to start
            </p>
            <p className="font-body text-sm text-[#A0A0A0] mt-1">{startDraftError}</p>
            <button
              data-testid="lobby-start-draft-error-dismiss"
              onClick={() => setStartDraftError(null)}
              className="mt-3 rounded-lg bg-[#252525] px-4 py-2 font-body text-sm text-[#F5F5F5] hover:bg-[#303030] transition-colors"
            >
              Dismiss and try again
            </button>
          </div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showCountdown && (
          <DraftCountdown
            firstTeam={
              lottoOrder.length > 0
                ? (league?.managers?.find(m => m.user_id === lottoOrder[0])?.team_name || "")
                : ""
            }
            firstLabel={isSnakeLeague ? "First pick" : "First to nominate"}
            leagueId={leagueId}
            onProgress={postPredraftProgress}
            onComplete={() => {
              setShowCountdown(false);
              showCountdownRef.current = false;
              nav(isSnakeLeague ? `/draft-snake/${leagueId}` : `/draft/${leagueId}`);
            }}
          />
        )}
      </AnimatePresence>

      {/* [RL-55] Empty-seat cascade modal - surfaces when commissioner clicks
          "Start Draft" with empty seats remaining. Owns its phase state
          internally; communicates back via the three callbacks. */}
      {showEmptySeatModal && seatStatus && (
        <EmptySeatModal
          leagueId={leagueId}
          filled={seatStatus.filled}
          empty={seatStatus.empty}
          numTeams={seatStatus.num_teams}
          onFill={onEmptySeatFill}
          onAdjusted={onEmptySeatAdjusted}
          onCancel={onEmptySeatCancel}
          isDark={isDark}
        />
      )}

      {/* Close League - red challenge flag, commissioner only */}
      {isCommissioner && (        <button
          data-testid="close-league-btn"
          onClick={() => setShowCloseConfirm(true)}
          disabled={closing}
          className="w-full py-4 bg-[#CC0000] hover:bg-[#AA0000] disabled:opacity-60 transition-all active:scale-[0.99] flex items-center justify-center gap-2 shrink-0"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="shrink-0">
            <rect x="5" y="3" width="9" height="9" rx="1" stroke="white" strokeWidth="2"/>
            <line x1="5" y1="3" x2="5" y2="21" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span className="font-display font-black text-base uppercase tracking-wider text-white">
            {closing ? "Closing League..." : "Close League"}
          </span>
        </button>
      )}
      {/* [TD-89 v2 Verdict 7.A] ConfirmModal replaces window.confirm() */}
      <ConfirmModal
        open={showCloseConfirm}
        onCancel={() => setShowCloseConfirm(false)}
        onConfirm={async () => { setShowCloseConfirm(false); await closeLeague(); }}
        title={`Close ${league?.name || "league"}?`}
        body={<>This permanently deletes the league and all its data. <strong>This cannot be undone.</strong></>}
        confirmLabel="Close League"
        destructive
        typedConfirmation="DELETE"
        isDark={isDark}
        testIdPrefix="draft-lobby-confirm-close"
      />
    </div>
    </>
  );
}
